import { useEffect, useMemo, useState } from "react";
import { IconRestore } from "@tabler/icons-react";
import { Badge, Button, Card, Checkbox, Group, Select, Stack, Text, useConfirmDialog } from "@/components/ui";
import { usePermisosCatalogo, useRolesAdmin } from "@/features/admin-roles/hooks/use-roles-admin";
import type { PermisoCatalogo } from "@/features/admin-roles/services/roles-service";
import { useAsignarModulosARol, useRestablecerModulosDeRol } from "../hooks/use-modulos";

/**
 * Vista POR ROL del gestor de módulos: elige un rol y habilita/deshabilita sus módulos
 * (checkboxes agrupados).
 *
 * Hotspot 89 (P46) · los módulos de los roles del SISTEMA son de SOLO LECTURA (igual que en
 * /admin/roles · el backend rechaza reasignarlos con ROL_SISTEMA_PERMISOS_SOLO_LECTURA): se
 * muestran deshabilitados y solo se ofrece «Restablecer a default» (vuelve a los valores del
 * código, p. ej. si una versión anterior los había editado). Solo los roles personalizados se
 * editan; para ajustar a una persona concreta está la pestaña «Por usuario».
 */
export function ModulosPorRolPanel() {
  const { data: roles } = useRolesAdmin();
  const { data: catalogo } = usePermisosCatalogo();
  const confirm = useConfirmDialog();

  const [rolId, setRolId] = useState<string>(() => roles[0]?.id ?? "");
  const rol = roles.find((r) => r.id === rolId) ?? roles[0];

  const [seleccion, setSeleccion] = useState<Set<string>>(() => new Set(rol?.permisos ?? []));
  // Al cambiar de rol (o al refrescarse tras guardar), sincroniza la selección local.
  useEffect(() => {
    setSeleccion(new Set(rol?.permisos ?? []));
  }, [rol?.id, rol?.permisos]);

  const grupos = useMemo(() => agrupar(catalogo), [catalogo]);
  const asignar = useAsignarModulosARol();
  const restablecer = useRestablecerModulosDeRol();

  if (!rol) return null;

  const toggle = (codigo: string, on: boolean) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (on) next.add(codigo);
      else next.delete(codigo);
      return next;
    });

  const soloLectura = rol.esSistema;
  const original = new Set(rol.permisos);
  const cambiado =
    seleccion.size !== original.size || [...seleccion].some((c) => !original.has(c));

  const confirmarRestablecer = async () => {
    const ok = await confirm({
      title: `Restablecer "${rol.nombre}"`,
      message: `Se devolverán los módulos de "${rol.nombre}" a los valores por defecto del sistema. Se perderán los cambios que le hayas hecho a este rol.`,
      confirmLabel: "Restablecer",
      confirmColor: "peligro",
    });
    if (ok) restablecer.mutate(rol.id);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Select
          label="Rol"
          wrapperClassName="w-80"
          options={roles.map((r) => ({ value: r.id, label: `${r.nombre}${r.esSistema ? " · Sistema" : ""}` }))}
          value={rolId}
          onValueChange={setRolId}
          searchable
        />
        <Group gap="xs">
          {rol.esSistema ? (
            <Button variant="light" color="slate" leftSection={<IconRestore size={15} />} loading={restablecer.isPending} onClick={confirmarRestablecer}>
              Restablecer a default
            </Button>
          ) : null}
          {soloLectura ? null : (
            <Button disabled={!cambiado} loading={asignar.isPending} onClick={() => asignar.mutate({ id: rol.id, permisos: [...seleccion] })}>
              Guardar módulos
            </Button>
          )}
        </Group>
      </Group>

      <div>
        <Group gap="xs" align="center">
          <Text size="sm" fw={600}>
            {rol.nombre}
          </Text>
          <Badge size="sm" color={rol.esSistema ? "info" : "slate"}>
            {rol.esSistema ? "Sistema" : "Personalizado"}
          </Badge>
          <Text size="xs" c="dimmed">
            {seleccion.size} módulo(s) · {rol.usuariosCount} usuario(s)
          </Text>
        </Group>
        <Text size="xs" c="dimmed" className="mt-1">
          {soloLectura
            ? "Rol del sistema · sus módulos los define el sistema y son de solo lectura. Para otro conjunto de módulos crea un rol personalizado, o ajusta a una persona en la pestaña «Por usuario»."
            : "Marca los módulos que este rol otorga. Aplica al instante en el servidor; en la sesión de cada usuario, tras su próximo inicio de sesión."}
        </Text>
      </div>

      <Card padding="lg">
        <div className="grid gap-6 sm:grid-cols-2">
          {grupos.map(([grupo, permisos]) => (
            <Stack key={grupo} gap={8}>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                {grupo}
              </Text>
              <Stack gap={6}>
                {permisos.map((p) => (
                  <Checkbox
                    key={p.codigo}
                    label={p.descripcion}
                    description={p.codigo}
                    checked={seleccion.has(p.codigo)}
                    disabled={soloLectura}
                    onCheckedChange={(v) => toggle(p.codigo, v === true)}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
        </div>
      </Card>
    </Stack>
  );
}

function agrupar(catalogo: PermisoCatalogo[]): Array<[string, PermisoCatalogo[]]> {
  const mapa = new Map<string, PermisoCatalogo[]>();
  for (const p of catalogo) {
    const lista = mapa.get(p.grupo) ?? [];
    lista.push(p);
    mapa.set(p.grupo, lista);
  }
  return [...mapa.entries()];
}
